/**
 * Company information — legal name, mailing address, phone, license number,
 * signatory, and the property management fee rate, plus the public contact
 * email that used to live inline in the gear dropdown (SettingsPanel.tsx).
 * Two singleton tables under one
 * form: `company_settings` (contactEmail, public-readable — see its own
 * doc comment) and `company_document_profile` (everything else, staff-only —
 * see CompanyDocumentProfile's doc comment in schema.prisma for why it's a
 * separate table). Both PATCH endpoints are `settings:write`, ADMIN only;
 * both GETs are any authenticated staff member, so this page renders for
 * anyone who reaches it and only the save button is refused for a
 * non-admin — "hiding is not protecting," same stance StaffLayout takes on
 * every nav link.
 */

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router';
import type { CompanyDocumentProfileDto, CompanySettingsDto } from 'shared';
import { api } from '../../lib/api.js';
import { Field, FormErrors, TextArea, TextInput } from '../../components/Form.js';

interface FormState {
  contactEmail: string;
  legalName: string;
  mailingLine1: string;
  mailingLine2: string;
  mailingCity: string;
  mailingState: string;
  mailingPostalCode: string;
  phone: string;
  licenseNumber: string;
  signatoryName: string;
  signatoryTitle: string;
  /** A percent string ("8" = 8.00%), not basis points — the form converts
   *  on submit. Empty string means "no fee configured", distinct from "0". */
  managementFeePercent: string;
  smsConsentDisclosureText: string;
}

const EMPTY: FormState = {
  contactEmail: '',
  legalName: '',
  mailingLine1: '',
  mailingLine2: '',
  mailingCity: '',
  mailingState: 'MI',
  mailingPostalCode: '',
  phone: '',
  licenseNumber: '',
  signatoryName: '',
  signatoryTitle: '',
  managementFeePercent: '',
  smsConsentDisclosureText: '',
};

export function CompanyInformationPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api<CompanySettingsDto>('/settings'),
      api<CompanyDocumentProfileDto>('/settings/document-profile'),
    ])
      .then(([settings, profile]) => {
        if (cancelled) return;
        setForm({
          contactEmail: settings.contactEmail ?? '',
          legalName: profile.legalName ?? '',
          mailingLine1: profile.mailingLine1 ?? '',
          mailingLine2: profile.mailingLine2 ?? '',
          mailingCity: profile.mailingCity ?? '',
          mailingState: profile.mailingState ?? 'MI',
          mailingPostalCode: profile.mailingPostalCode ?? '',
          phone: profile.phone ?? '',
          licenseNumber: profile.licenseNumber ?? '',
          signatoryName: profile.signatoryName ?? '',
          signatoryTitle: profile.signatoryTitle ?? '',
          managementFeePercent:
            profile.managementFeeBasisPoints != null
              ? String(profile.managementFeeBasisPoints / 100)
              : '',
          smsConsentDisclosureText: profile.smsConsentDisclosureText ?? '',
        });
        setLoaded(true);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setLoadError(caught);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setForm((previous) => ({ ...previous, [key]: value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setSaveError(null);
    setSaved(false);
    try {
      await Promise.all([
        api('/settings', {
          method: 'PATCH',
          body: JSON.stringify({ contactEmail: form.contactEmail.trim() || null }),
        }),
        api('/settings/document-profile', {
          method: 'PATCH',
          body: JSON.stringify({
            legalName: form.legalName.trim() || null,
            mailingLine1: form.mailingLine1.trim() || null,
            mailingLine2: form.mailingLine2.trim() || null,
            mailingCity: form.mailingCity.trim() || null,
            mailingState: form.mailingState.trim() || null,
            mailingPostalCode: form.mailingPostalCode.trim() || null,
            phone: form.phone.trim() || null,
            licenseNumber: form.licenseNumber.trim() || null,
            signatoryName: form.signatoryName.trim() || null,
            signatoryTitle: form.signatoryTitle.trim() || null,
            managementFeeBasisPoints:
              form.managementFeePercent.trim() === ''
                ? null
                : Math.round(Number(form.managementFeePercent) * 100),
            smsConsentDisclosureText: form.smsConsentDisclosureText.trim() || null,
          }),
        }),
      ]);
      setSaved(true);
    } catch (caught) {
      setSaveError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <FormErrors error={loadError} />;
  if (!loaded) return <p className="muted">Loading…</p>;

  return (
    <section className="stack">
      <div className="row">
        <h1>Company Information</h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff')}
        >
          ← Dashboard
        </button>
      </div>

      <p className="muted">
        Feeds the merge fields available to notice and lease agreement templates (see Document
        Templates), plus the public contact email shown on listing pages.
      </p>

      <form className="stack card" onSubmit={(event) => void submit(event)}>
        <FormErrors error={saveError} />

        <Field
          label="Public contact email"
          name="contactEmail"
          hint="Shown to prospective renters on each listing's page as a Contact us link. Leave blank to hide that option."
        >
          <TextInput
            name="contactEmail"
            type="email"
            value={form.contactEmail}
            onChange={set('contactEmail')}
            placeholder="leasing@example.com"
          />
        </Field>

        <Field label="Legal company name" name="legalName">
          <TextInput name="legalName" value={form.legalName} onChange={set('legalName')} />
        </Field>

        <Field label="Mailing address, line 1" name="mailingLine1">
          <TextInput name="mailingLine1" value={form.mailingLine1} onChange={set('mailingLine1')} />
        </Field>

        <Field label="Mailing address, line 2" name="mailingLine2">
          <TextInput name="mailingLine2" value={form.mailingLine2} onChange={set('mailingLine2')} />
        </Field>

        <Field label="City" name="mailingCity">
          <TextInput name="mailingCity" value={form.mailingCity} onChange={set('mailingCity')} />
        </Field>

        <Field label="State" name="mailingState">
          <TextInput name="mailingState" value={form.mailingState} onChange={set('mailingState')} />
        </Field>

        <Field label="ZIP code" name="mailingPostalCode">
          <TextInput
            name="mailingPostalCode"
            value={form.mailingPostalCode}
            onChange={set('mailingPostalCode')}
          />
        </Field>

        <Field label="Phone" name="phone">
          <TextInput name="phone" type="tel" value={form.phone} onChange={set('phone')} />
        </Field>

        <Field label="License / registration number" name="licenseNumber">
          <TextInput
            name="licenseNumber"
            value={form.licenseNumber}
            onChange={set('licenseNumber')}
          />
        </Field>

        <Field
          label="Signatory name"
          name="signatoryName"
          hint="Printed on generated lease agreements' signature block."
        >
          <TextInput
            name="signatoryName"
            value={form.signatoryName}
            onChange={set('signatoryName')}
          />
        </Field>

        <Field label="Signatory title" name="signatoryTitle">
          <TextInput
            name="signatoryTitle"
            value={form.signatoryTitle}
            onChange={set('signatoryTitle')}
          />
        </Field>

        <Field
          label="Property management fee (%)"
          name="managementFeePercent"
          hint="Deducted from each owner's share of every rent payment as its own disbursement line item. Leave blank for no fee."
        >
          <TextInput
            name="managementFeePercent"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.managementFeePercent}
            onChange={set('managementFeePercent')}
            placeholder="8.00"
          />
        </Field>

        <Field
          label="SMS consent disclosure"
          name="smsConsentDisclosureText"
          hint="Shown to staff in the SMS consent dialog when recording a tenant's decision to receive text messages. Leave blank to show no disclosure text."
        >
          <TextArea
            name="smsConsentDisclosureText"
            value={form.smsConsentDisclosureText}
            onChange={set('smsConsentDisclosureText')}
            rows={3}
          />
        </Field>

        {saved ? <p className="muted">Saved.</p> : null}

        <div className="row">
          <button type="submit" className="button" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </section>
  );
}
